import { useCallback, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { AxiosResponse } from "axios";
import { ServicesURL } from "@utils/utilsApi";
import { axiosInstance } from "src/services/config";

const POLLING_INITIAL_INTERVAL = 5000;
const POLLING_FLAT_DURATION = 120000;
const POLLING_BACKOFF_FACTOR = 1.5;
const POLLING_MAX_INTERVAL = 45000;
const POLLING_MAX_DURATION = 3600000;
const POLLING_MAX_CONSECUTIVE_ERRORS = 3;
const DOWNLOAD_FILE_MAX_ATTEMPTS = 3;

type AsyncJobStatus = "STARTED" | "COMPLETED" | "ERROR";

interface AsyncJobResponse {
  requestId?: number;
  status?: AsyncJobStatus;
  errorMessage?: string;
  fileName?: string;
}

const registryFailure = (message: string, response?: AxiosResponse) => {
  const failure: any = new Error(message);
  failure.response = response ?? { data: { errorMessage: message } };
  return failure;
};

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const triggerFileDownload = (downloadUrl: string) => {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = downloadUrl;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, 60000);
};

export const useRegistryFile = (
  corporateId: string | number,
  editionId: string | number,
) => {
  const cancelledRef = useRef(false);
  const uploadInFlightRef = useRef(false);
  const downloadInFlightRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const startJob = async (
    request: () => Promise<AxiosResponse<AsyncJobResponse>>,
    label: string,
  ) => {
    const response = await request();
    if (response.data?.status === "ERROR") {
      throw registryFailure(
        response.data?.errorMessage || `Virtual register ${label} failed`,
        response,
      );
    }
    const requestId = response.data?.requestId;
    if (!requestId) {
      throw registryFailure(
        `Virtual register ${label}: requestId missing from start response`,
        response,
      );
    }
    return requestId;
  };

  const pollJob = async <T>(
    statusUrl: string,
    label: string,
    onCompleted: (response: AxiosResponse<AsyncJobResponse>) => Promise<T>,
    maxCompletedFailures = 1,
  ): Promise<T> => {
    const startedAt = Date.now();
    let delay = POLLING_INITIAL_INTERVAL;
    let consecutiveErrors = 0;
    let completedFailures = 0;

    for (;;) {
      await wait(delay);
      if (cancelledRef.current) {
        throw registryFailure(`Virtual register ${label} cancelled`);
      }
      const elapsed = Date.now() - startedAt;

      let response: AxiosResponse<AsyncJobResponse> | undefined;
      try {
        response = await axiosInstance.get(statusUrl);
        consecutiveErrors = 0;
      } catch (error) {
        consecutiveErrors += 1;
        if (
          consecutiveErrors >= POLLING_MAX_CONSECUTIVE_ERRORS ||
          elapsed >= POLLING_MAX_DURATION
        ) {
          throw error;
        }
        console.warn(
          `Virtual register ${label}: polling error ${consecutiveErrors}/${POLLING_MAX_CONSECUTIVE_ERRORS}, retrying`,
          error,
        );
      }

      if (response) {
        const { status, errorMessage } = response.data || {};
        if (status === "COMPLETED") {
          try {
            return await onCompleted(response);
          } catch (error) {
            completedFailures += 1;
            if (completedFailures >= maxCompletedFailures) {
              throw error;
            }
            console.warn(
              `Virtual register ${label}: completion step failed ${completedFailures}/${maxCompletedFailures}, retrying`,
              error,
            );
          }
        } else if (status === "ERROR") {
          throw registryFailure(
            errorMessage || `Virtual register ${label} failed`,
            response,
          );
        }
      }

      if (elapsed >= POLLING_MAX_DURATION) {
        throw registryFailure(
          `Virtual register ${label} timed out while polling`,
        );
      }
      if (elapsed >= POLLING_FLAT_DURATION) {
        delay = Math.min(delay * POLLING_BACKOFF_FACTOR, POLLING_MAX_INTERVAL);
      }
    }
  };

  const uploadRegistryFile = useCallback(
    async (file: File) => {
      if (uploadInFlightRef.current) {
        throw registryFailure("Virtual register upload already in progress");
      }
      uploadInFlightRef.current = true;
      try {
        const dotIndex = file.name.lastIndexOf(".");
        const nameNoExt =
          dotIndex >= 0 ? file.name.slice(0, dotIndex) : file.name;
        const ext = dotIndex >= 0 ? file.name.slice(dotIndex + 1) : "";
        const filename = `${nameNoExt}_${dayjs().format("YYYYMMDDHHmmss")}${
          ext ? `.${ext}` : ""
        }`;

        const body = new FormData();
        body.append("data", file, filename);

        const requestId = await startJob(
          () =>
            axiosInstance.post(
              `${ServicesURL.postVirtualRegisterUploadAsync}?corporateId=${corporateId}&editionId=${editionId}&filename=${encodeURIComponent(filename)}`,
              body,
              { headers: { "Content-Type": "multipart/form-data" } },
            ),
          "upload",
        );

        return await pollJob(
          `${ServicesURL.getVirtualRegisterUploadStatus}?requestId=${requestId}&corporateId=${corporateId}`,
          "upload",
          async (response) => response,
        );
      } finally {
        uploadInFlightRef.current = false;
      }
    },
    [corporateId, editionId],
  );

  const downloadRegistryFile = useCallback(async () => {
    if (downloadInFlightRef.current) {
      return;
    }
    downloadInFlightRef.current = true;
    try {
      const requestId = await startJob(
        () =>
          axiosInstance.post(
            `${ServicesURL.postVirtualRegisterDownloadAsync}?corporateId=${corporateId}&editionId=${editionId}`,
          ),
        "download",
      );

      await pollJob(
        `${ServicesURL.getVirtualRegisterDownloadStatus}?requestId=${requestId}&corporateId=${corporateId}`,
        "download",
        async (statusResponse) => {
          const fileResponse = await axiosInstance.get(
            `${ServicesURL.getVirtualRegisterDownloadFile}?corporateId=${corporateId}&editionId=${editionId}&fileName=${encodeURIComponent(statusResponse.data?.fileName || "")}`,
          );
          const downloadUrl = fileResponse.data?.public_url;
          if (!downloadUrl) {
            throw registryFailure(
              "Virtual register download: file URL missing from response",
              fileResponse,
            );
          }
          triggerFileDownload(downloadUrl);
        },
        DOWNLOAD_FILE_MAX_ATTEMPTS,
      );
    } finally {
      downloadInFlightRef.current = false;
    }
  }, [corporateId, editionId]);

  return { uploadRegistryFile, downloadRegistryFile };
};
